import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { 
  User, 
  Lock, 
  Phone, 
  Camera, 
  Trash2, 
  Save,
  Eye,
  EyeOff,
  Check,
  X,
  AlertCircle,
  Shield,
  Mail,
  Upload,
  Tag,
  RefreshCw,
  Edit2,
  History,
  Calendar,
  CheckCircle2,
  Circle,
  BellRing,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getPermissionState, isPushSupported, subscribeToPush, unsubscribeFromPush } from '../utils/pushNotifications';

const NOTIFICATION_CATEGORIES = [
  { key: 'task_updates', label: 'Task Updates', description: 'Assignment, reassignment and status changes' },
  { key: 'comments_mentions', label: 'Comments & Mentions', description: 'New comments and @mentions on your tasks' },
  { key: 'approvals', label: 'Approvals', description: 'Manager, developer and UI/UX approval decisions' },
  { key: 'social_media', label: 'Social Media', description: 'Post assignments, scheduling, publishing and clashes' },
  { key: 'overdue_reminders', label: 'Overdue Reminders', description: 'Daily reminders for tasks past their due date' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country codes with flags
const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi', flag: '🇸🇦' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
];

const MyProfile = () => {
  const { user, token, logout } = useAuth();
  const { isManager } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    mobile_country_code: '+91',
    profile_picture: null,
    pending_email: null,
    assigned_brands: []
  });
  
  // Password change state
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', message: '' });
  
  // Profile picture state
  const fileInputRef = useRef(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [picturePreview, setPicturePreview] = useState(null);
  
  // Email change state
  const [emailModal, setEmailModal] = useState(false);
  const [emailStep, setEmailStep] = useState('request'); // 'request' or 'verify'
  const [emailData, setEmailData] = useState({
    new_email: '',
    password: '',
    otp: ''
  });
  const [pendingEmail, setPendingEmail] = useState(null);
  
  // Brand assignment state
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  
  // Activity log state
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(true);
  const [pushPermission, setPushPermission] = useState(getPermissionState());
  const [pushBusy, setPushBusy] = useState(false);

  // Department name — resolved from the admin-managed department_id on the
  // user record. Read-only here; Department is set via User Management.
  const [departmentName, setDepartmentName] = useState(null);
  useEffect(() => {
    if (!user?.department_id || !token) return;
    axios.get(`${API}/rbac/departments/${user.department_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartmentName(res.data?.name))
      .catch(() => setDepartmentName('—'));
  }, [user?.department_id, token]);

  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await axios.get(`${API}/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifPrefs(res.data);
      } catch (error) {
        toast.error('Failed to load notification preferences');
      } finally {
        setNotifPrefsLoading(false);
      }
    };
    loadPrefs();
  }, [token]);

  const togglePreference = async (field) => {
    const nextValue = !notifPrefs[field];
    setNotifPrefs(prev => ({ ...prev, [field]: nextValue }));
    try {
      const res = await axios.put(`${API}/notification-preferences`, { [field]: nextValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifPrefs(res.data);
    } catch (error) {
      setNotifPrefs(prev => ({ ...prev, [field]: !nextValue }));
      toast.error('Failed to update preference');
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush(token);
      setPushPermission(getPermissionState());
      toast.success('Push notifications enabled on this browser');
    } catch (error) {
      toast.error(error.message || 'Could not enable push notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush(token);
      toast.success('Push notifications disabled on this browser');
    } catch (error) {
      toast.error('Could not disable push notifications');
    } finally {
      setPushBusy(false);
    }
  };
  
  // Load user data
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        mobile_number: user.mobile_number || '',
        mobile_country_code: user.mobile_country_code || '+91',
        profile_picture: user.profile_picture || null,
        pending_email: user.pending_email || null,
        assigned_brands: user.assigned_brands || []
      });
      setPendingEmail(user.pending_email || null);
      setSelectedBrands(user.assigned_brands || []);
    }
  }, [user]);

  // Load available brands
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableBrands(response.data.brands || []);
      } catch (error) {
        console.error('Failed to load brands:', error);
      }
    };
    if (token) loadBrands();
  }, [token]);

  // Load recent activity
  useEffect(() => {
    const loadActivity = async () => {
      setActivityLoading(true);
      try {
        const response = await axios.get(`${API}/audit-logs/my-activity?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecentActivity(response.data.logs || []);
      } catch (error) {
        console.error('Failed to load activity:', error);
      } finally {
        setActivityLoading(false);
      }
    };
    if (token) loadActivity();
  }, [token]);

  // ================= Reporting hierarchy =================
  const [reportsToUser, setReportsToUser] = useState(null);
  const [myTeamSummary, setMyTeamSummary] = useState(null);

  useEffect(() => {
    const loadHierarchy = async () => {
      if (!token || !user?.id) return;
      try {
        // Fetch reports_to
        if (user.reports_to_user_id) {
          const r = await axios.get(`${API}/users/${user.reports_to_user_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setReportsToUser(r.data);
        }
        // Fetch my-team (returns empty for non-leads, which is fine)
        const t = await axios.get(`${API}/users/my-team`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyTeamSummary(t.data);
      } catch (e) {
        // Non-fatal
      }
    };
    loadHierarchy();
  }, [token, user?.id, user?.reports_to_user_id]);

  // Check password strength
  useEffect(() => {
    const checkStrength = async () => {
      if (passwordData.new_password.length >= 1) {
        try {
          const response = await axios.get(
            `${API}/profile/password-strength?password=${encodeURIComponent(passwordData.new_password)}`
          );
          setPasswordStrength(response.data);
        } catch (error) {
          setPasswordStrength({ strength: 'weak', message: 'Unable to check strength' });
        }
      } else {
        setPasswordStrength({ strength: '', message: '' });
      }
    };
    
    const debounce = setTimeout(checkStrength, 300);
    return () => clearTimeout(debounce);
  }, [passwordData.new_password]);

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      case 'very_strong': return 'bg-emerald-600';
      default: return 'bg-gray-200';
    }
  };

  const getStrengthWidth = (strength) => {
    switch (strength) {
      case 'weak': return 'w-1/4';
      case 'medium': return 'w-2/4';
      case 'strong': return 'w-3/4';
      case 'very_strong': return 'w-full';
      default: return 'w-0';
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(
        `${API}/profile/update`,
        {
          full_name: profileData.full_name,
          mobile_number: profileData.mobile_number || null,
          mobile_country_code: profileData.mobile_country_code || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile updated successfully!');
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!passwordStrength.is_valid) {
      toast.error(passwordStrength.message);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/profile/change-password`,
        passwordData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      if (response.data.require_relogin) {
        setTimeout(() => logout(), 1500);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile picture upload
  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, PNG, and WEBP are allowed.');
      return;
    }
    
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 15MB.');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => setPicturePreview(reader.result);
    reader.readAsDataURL(file);
    
    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/profile/picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Profile picture uploaded!');
      setProfileData(prev => ({ ...prev, profile_picture: response.data.profile_picture }));
      setPicturePreview(null);
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload picture';
      toast.error(message);
      setPicturePreview(null);
    } finally {
      setUploadingPicture(false);
    }
  };

  // Handle profile picture removal
  const handleRemovePicture = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) return;
    
    setLoading(true);
    
    try {
      await axios.delete(`${API}/profile/picture`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profile picture removed');
      setProfileData(prev => ({ ...prev, profile_picture: null }));
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove picture';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle email change request
  const handleEmailChangeRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/profile/email/request-change`,
        { new_email: emailData.new_email, password: emailData.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setPendingEmail(response.data.pending_email);
      setEmailStep('verify');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to request email change';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/profile/email/verify-otp`,
        { otp: emailData.otp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setEmailModal(false);
      setEmailData({ new_email: '', password: '', otp: '' });
      setEmailStep('request');
      
      if (response.data.require_relogin) {
        setTimeout(() => logout(), 1500);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to verify OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/profile/email/resend-otp`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('New OTP sent to your pending email');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel email change
  const handleCancelEmailChange = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/profile/email/cancel-change`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Email change request cancelled');
      setPendingEmail(null);
      setEmailStep('request');
      setEmailModal(false);
    } catch (error) {
      toast.error('Failed to cancel email change');
    } finally {
      setLoading(false);
    }
  };

  // Handle brand toggle
  const toggleBrand = (brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  // Handle save brands (for managers only)
  const handleSaveBrands = async () => {
    if (!isManager) {
      toast.error('Only managers can update brand assignments');
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/profile/brands`,
        selectedBrands,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Brand assignments updated');
      setBrandModalOpen(false);
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update brands';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getProfilePictureUrl = () => {
    if (picturePreview) return picturePreview;
    if (profileData.profile_picture) {
      if (profileData.profile_picture.startsWith('http')) return profileData.profile_picture;
      if (profileData.profile_picture.startsWith('data:')) return profileData.profile_picture;
      return `${process.env.REACT_APP_BACKEND_URL}${profileData.profile_picture}`;
    }
    return null;
  };

  // Role identity-derived (RBAC). Kept inline previously via role_level — now from usePermissions().

  // Profile completion calculation
  const getProfileCompletion = () => {
    const items = [
      { label: 'Full Name', completed: !!profileData.full_name },
      { label: 'Email', completed: !!profileData.email },
      { label: 'Mobile Number', completed: !!profileData.mobile_number },
      { label: 'Profile Picture', completed: !!profileData.profile_picture },
      { label: 'Brand Assignments', completed: profileData.assigned_brands && profileData.assigned_brands.length > 0 }
    ];
    const completedCount = items.filter(i => i.completed).length;
    const percentage = Math.round((completedCount / items.length) * 100);
    return { items, completedCount, total: items.length, percentage };
  };

  const profileCompletion = getProfileCompletion();

  // Format activity timestamp
  const formatActivityTime = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return timestamp;
    }
  };

  // Get activity action label
  const getActivityLabel = (action) => {
    const labels = {
      'password_change': 'Changed password',
      'email_change_requested': 'Requested email change',
      'email_change_completed': 'Changed email address',
      'mobile_update': 'Updated mobile number',
      'profile_picture_upload': 'Uploaded profile picture',
      'profile_picture_remove': 'Removed profile picture',
      'profile_update': 'Updated profile',
      'brand_assignment_self_update': 'Updated brand assignments'
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="my-profile-page">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title flex items-center gap-3">
          <User className="text-cyan-500" size={32} />
          My Profile
        </h1>
        <p className="page-subtitle">Manage your account settings and security</p>
      </div>

      {/* Profile Completion Progress */}
      {profileCompletion.percentage < 100 && (
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200 p-6" data-testid="profile-completion-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Complete Your Profile</h2>
            <span className="text-2xl font-bold text-cyan-600">{profileCompletion.percentage}%</span>
          </div>
          
          <div className="w-full h-3 bg-white rounded-full overflow-hidden mb-4">
            <div 
              className="h-full btn-cta transition-all duration-500"
              style={{ width: `${profileCompletion.percentage}%` }}
            />
          </div>
          
          <div className="flex flex-wrap gap-3">
            {profileCompletion.items.map((item) => (
              <div 
                key={item.label}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                  item.completed 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-white text-slate-500 border border-slate-200'
                }`}
              >
                {item.completed ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                {item.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reporting Hierarchy Card */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        data-testid="reporting-hierarchy-card"
      >
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <User size={20} className="text-cyan-500" />
          Reporting Hierarchy
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Reports To */}
          <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Reports To</p>
            {reportsToUser ? (
              <div className="flex items-center gap-3" data-testid="reports-to-block">
                <div className="w-10 h-10 rounded-full btn-cta flex items-center justify-center text-white font-semibold">
                  {reportsToUser.full_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{reportsToUser.full_name}</p>
                  <p className="text-xs text-slate-500">Team Lead</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">
                {user?.role_level === 'Manager' || user?.role_level === 'Admin'
                  ? 'Organization-wide scope. No direct manager.'
                  : 'No Team Lead assigned. Ask an admin to link you to your Team Lead.'}
              </p>
            )}
          </div>

          {/* My Team (visible for anyone who has direct reports) */}
          <div className="p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid="my-team-block">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Team Members</p>
            {myTeamSummary && myTeamSummary.summary?.total_members > 0 ? (
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {myTeamSummary.summary.total_members} Member{myTeamSummary.summary.total_members !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {myTeamSummary.summary.active_members} active · {myTeamSummary.summary.total_tasks} tasks · {myTeamSummary.summary.completion_rate}% completion rate
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">You do not have any direct reports.</p>
            )}
          </div>
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Camera size={20} className="text-cyan-500" />
          Profile Picture
        </h2>
        
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-28 h-28 rounded-full overflow-hidden btn-cta flex items-center justify-center">
              {getProfilePictureUrl() ? (
                <img src={getProfilePictureUrl()} alt="Profile" className="w-full h-full object-cover" data-testid="profile-picture-preview" />
              ) : (
                <span className="text-4xl font-bold text-white">{user?.full_name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            {uploadingPicture && (
              <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Upload a profile picture. JPG, PNG, or WEBP. Max 15MB.</p>
            <div className="flex gap-2">
              <input type="file" ref={fileInputRef} onChange={handlePictureUpload} accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="profile-picture-input" />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingPicture} data-testid="upload-picture-button">
                <Upload size={16} className="mr-2" />
                {profileData.profile_picture ? 'Change Picture' : 'Upload Picture'}
              </Button>
              {profileData.profile_picture && (
                <Button type="button" variant="outline" onClick={handleRemovePicture} className="text-red-600 hover:text-red-700 hover:bg-red-50" disabled={loading} data-testid="remove-picture-button">
                  <Trash2 size={16} className="mr-2" />
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <User size={20} className="text-cyan-500" />
          Account Information
        </h2>
        
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input id="full_name" value={profileData.full_name} onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))} placeholder="Your full name" required minLength={2} data-testid="profile-name-input" />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="flex gap-2">
                <Input id="email" type="email" value={profileData.email} disabled className="bg-slate-50 flex-1" data-testid="profile-email-input" />
                <Button type="button" variant="outline" size="icon" onClick={() => setEmailModal(true)} data-testid="change-email-button" title="Change Email">
                  <Edit2 size={16} />
                </Button>
              </div>
              {pendingEmail && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle size={12} />
                  Pending verification: {pendingEmail}
                </p>
              )}
            </div>
          </div>
          
          {/* Mobile Number */}
          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile Number</Label>
            <div className="flex gap-2">
              <Select value={profileData.mobile_country_code || '+91'} onValueChange={(v) => setProfileData(prev => ({ ...prev, mobile_country_code: v }))}>
                <SelectTrigger className="w-32" data-testid="country-code-select">
                  <SelectValue placeholder="Select code" />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((cc) => (
                    <SelectItem key={cc.code} value={cc.code}>{cc.flag} {cc.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input id="mobile" type="tel" value={profileData.mobile_number} onChange={(e) => setProfileData(prev => ({ ...prev, mobile_number: e.target.value.replace(/\D/g, '') }))} placeholder="Enter mobile number" className="flex-1" data-testid="profile-mobile-input" />
            </div>
            <p className="text-xs text-slate-500">Your mobile number will be used for account recovery and notifications</p>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={loading} data-testid="save-profile-button">
              <Save size={16} className="mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Brand Assignments Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Tag size={20} className="text-cyan-500" />
          Brand Assignments
        </h2>
        
        <p className="text-sm text-slate-600 mb-4">
          {isManager 
            ? 'Select brands you want to focus on. This filters tasks by default but you can view other brands.'
            : 'Your assigned brands determine which tasks appear by default in your views.'}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {profileData.assigned_brands && profileData.assigned_brands.length > 0 ? (
            profileData.assigned_brands.map(brand => (
              <span key={brand} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-sm font-medium">
                <Tag size={14} />
                {brand}
              </span>
            ))
          ) : (
            <span className="text-sm text-slate-500 italic">No brands assigned - viewing all tasks</span>
          )}
        </div>
        
        {isManager && (
          <Button variant="outline" onClick={() => setBrandModalOpen(true)} data-testid="edit-brands-button">
            <Edit2 size={16} className="mr-2" />
            Edit Brand Assignments
          </Button>
        )}
        
        {!isManager && (
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <p>Contact your manager to update your brand assignments.</p>
          </div>
        )}
      </div>

      {/* Security Settings Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Shield size={20} className="text-cyan-500" />
          Security Settings
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <h3 className="font-medium text-slate-800">Password</h3>
              <p className="text-sm text-slate-600">Change your account password</p>
            </div>
            <Button variant="outline" onClick={() => setPasswordModal(true)} data-testid="change-password-button">
              <Lock size={16} className="mr-2" />
              Change Password
            </Button>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-500 mt-0.5" size={20} />
              <div>
                <h4 className="font-medium text-amber-800">Security Tip</h4>
                <p className="text-sm text-amber-700">Use a strong, unique password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Preferences Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="notification-preferences-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <BellRing size={20} className="text-cyan-500" />
          Notifications
        </h2>
        <p className="text-sm text-slate-500 mb-4">Choose what you get notified about, in-app and via browser push.</p>

        <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
          <div className="grid grid-cols-[1fr,5rem,5rem] bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wide">
            <span>Category</span>
            <span className="text-center">In-App</span>
            <span className="text-center">Push</span>
          </div>
          {notifPrefsLoading || !notifPrefs ? (
            <div className="px-4 py-6 text-sm text-slate-500">Loading preferences…</div>
          ) : (
            NOTIFICATION_CATEGORIES.map((cat) => (
              <div key={cat.key} className="grid grid-cols-[1fr,5rem,5rem] items-center px-4 py-3 border-t border-slate-100">
                <div>
                  <p className="text-sm font-medium text-slate-800">{cat.label}</p>
                  <p className="text-xs text-slate-500">{cat.description}</p>
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={!!notifPrefs[`${cat.key}_inapp`]}
                    onCheckedChange={() => togglePreference(`${cat.key}_inapp`)}
                    data-testid={`notif-pref-${cat.key}-inapp`}
                  />
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={!!notifPrefs[`${cat.key}_push`]}
                    onCheckedChange={() => togglePreference(`${cat.key}_push`)}
                    data-testid={`notif-pref-${cat.key}-push`}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div>
            <h3 className="font-medium text-slate-800">Push Notifications on This Device</h3>
            <p className="text-sm text-slate-600">
              {!isPushSupported()
                ? 'Not supported in this browser'
                : pushPermission === 'granted'
                  ? 'Enabled — you can turn this off below'
                  : pushPermission === 'denied'
                    ? 'Blocked in browser settings — re-enable from your browser\'s site permissions'
                    : 'Not enabled on this browser/device yet'}
            </p>
          </div>
          {isPushSupported() && pushPermission !== 'denied' && (
            pushPermission === 'granted' ? (
              <Button variant="outline" onClick={handleDisablePush} disabled={pushBusy} data-testid="disable-push-button">
                {pushBusy ? 'Working…' : 'Disable'}
              </Button>
            ) : (
              <Button onClick={handleEnablePush} disabled={pushBusy} data-testid="enable-push-button">
                {pushBusy ? 'Working…' : 'Enable'}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Role Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Mail size={20} className="text-cyan-500" />
          Role Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 mb-1">Role Level</p>
            <p className="font-semibold text-slate-800">{user?.role_level}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 mb-1">Account Status</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user?.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {user?.status || 'Active'}
            </span>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="profile-department-field">
            <p className="text-sm text-slate-600 mb-1">Department</p>
            <p className="font-semibold text-slate-800">
              {user?.department_id ? (departmentName || 'Loading…') : <span className="text-slate-400 italic font-normal">Not assigned</span>}
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="profile-date-joined-field">
            <p className="text-sm text-slate-600 mb-1">Date Joined</p>
            <p className="font-semibold text-slate-800">
              {user?.created_at ? format(parseISO(user.created_at), 'MMMM d, yyyy') : <span className="text-slate-400 italic font-normal">—</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="recent-activity-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <History size={20} className="text-cyan-500" />
          Recent Activity
        </h2>
        
        {activityLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
          </div>
        ) : recentActivity.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <History size={32} className="mx-auto mb-2 text-slate-300" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                  <History size={14} className="text-cyan-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 capitalize">
                    {getActivityLabel(activity.action)}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar size={12} />
                    {formatActivityTime(activity.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={20} className="text-cyan-500" />
              Change Password
            </DialogTitle>
            <DialogDescription>Enter your current password and choose a new one. You&rsquo;ll be logged out after changing your password.</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input id="current_password" type={showPasswords.current ? 'text' : 'password'} value={passwordData.current_password} onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))} placeholder="Enter current password" required data-testid="current-password-input" />
                <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input id="new_password" type={showPasswords.new ? 'text' : 'password'} value={passwordData.new_password} onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))} placeholder="Enter new password" required minLength={8} data-testid="new-password-input" />
                <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              
              {passwordData.new_password && (
                <div className="space-y-1">
                  <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength.strength)} ${getStrengthWidth(passwordStrength.strength)}`} />
                  </div>
                  <p className={`text-xs ${passwordStrength.strength === 'weak' ? 'text-red-600' : passwordStrength.strength === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>
                    {passwordStrength.message}
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input id="confirm_password" type={showPasswords.confirm ? 'text' : 'password'} value={passwordData.confirm_password} onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))} placeholder="Confirm new password" required data-testid="confirm-password-input" />
                <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                <p className="text-xs text-red-600 flex items-center gap-1"><X size={12} /> Passwords do not match</p>
              )}
              {passwordData.confirm_password && passwordData.new_password === passwordData.confirm_password && passwordData.confirm_password.length >= 8 && (
                <p className="text-xs text-green-600 flex items-center gap-1"><Check size={12} /> Passwords match</p>
              )}
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium mb-1">Password Requirements:</p>
              <ul className="space-y-0.5 list-disc list-inside">
                <li>At least 8 characters long</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include at least one number</li>
                <li>Include at least one special character (!@#$%^&*)</li>
              </ul>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPasswordModal(false); setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); }}>Cancel</Button>
              <Button type="submit" disabled={loading || !passwordStrength.is_valid || passwordData.new_password !== passwordData.confirm_password} data-testid="submit-password-change">
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Change Modal */}
      <Dialog open={emailModal} onOpenChange={(open) => { if (!open) { setEmailModal(false); setEmailStep('request'); setEmailData({ new_email: '', password: '', otp: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-cyan-500" />
              Change Email Address
            </DialogTitle>
            <DialogDescription>
              {emailStep === 'request' 
                ? 'Enter your new email address and current password to receive a verification OTP.'
                : `Enter the 6-digit OTP sent to ${pendingEmail}`}
            </DialogDescription>
          </DialogHeader>
          
          {emailStep === 'request' ? (
            <form onSubmit={handleEmailChangeRequest} className="space-y-4">
              <div className="space-y-2">
                <Label>Current Email</Label>
                <Input value={profileData.email} disabled className="bg-slate-50" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_email">New Email Address</Label>
                <Input id="new_email" type="email" value={emailData.new_email} onChange={(e) => setEmailData(prev => ({ ...prev, new_email: e.target.value }))} placeholder="Enter new email address" required data-testid="new-email-input" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email_password">Current Password</Label>
                <Input id="email_password" type="password" value={emailData.password} onChange={(e) => setEmailData(prev => ({ ...prev, password: e.target.value }))} placeholder="Enter your current password" required data-testid="email-change-password-input" />
                <p className="text-xs text-slate-500">Password required to confirm your identity</p>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEmailModal(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} data-testid="request-email-change-button">
                  {loading ? 'Sending OTP...' : 'Send Verification OTP'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-center">
                <Mail className="mx-auto text-cyan-500 mb-2" size={32} />
                <p className="text-sm text-cyan-800">
                  We&rsquo;ve sent a 6-digit OTP to <strong>{pendingEmail}</strong>
                </p>
                <p className="text-xs text-cyan-600 mt-1">The OTP expires in 10 minutes</p>
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  📧 Can&rsquo;t find the email? Please check your spam/junk folder.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="otp">Enter OTP</Label>
                <Input id="otp" type="text" value={emailData.otp} onChange={(e) => setEmailData(prev => ({ ...prev, otp: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="000000" maxLength={6} className="text-center text-2xl tracking-widest font-mono" required data-testid="email-otp-input" />
              </div>
              
              <div className="flex justify-center">
                <button type="button" onClick={handleResendOTP} disabled={loading} className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
                  <RefreshCw size={14} />
                  Resend OTP
                </button>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={handleCancelEmailChange} className="text-red-600" disabled={loading}>
                  Cancel Change
                </Button>
                <Button type="submit" disabled={loading || emailData.otp.length !== 6} data-testid="verify-email-otp-button">
                  {loading ? 'Verifying...' : 'Verify & Change Email'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Brand Assignment Modal */}
      <Dialog open={brandModalOpen} onOpenChange={setBrandModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} className="text-cyan-500" />
              Edit Brand Assignments
            </DialogTitle>
            <DialogDescription>Select brands to focus on. Tasks from these brands will appear by default in your views.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-lg border border-slate-200">
              {availableBrands.map(brand => (
                <button
                  key={brand}
                  type="button"
                  onClick={() => toggleBrand(brand)}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedBrands.includes(brand)
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white text-slate-700 border border-slate-300 hover:border-cyan-300'
                  }`}
                  data-testid={`brand-toggle-${brand.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {selectedBrands.includes(brand) && <Check size={14} />}
                  {brand}
                </button>
              ))}
            </div>
            
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>{selectedBrands.length} brand(s) selected</span>
              <button type="button" onClick={() => setSelectedBrands([])} className="text-red-600 hover:text-red-700">Clear all</button>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setBrandModalOpen(false); setSelectedBrands(profileData.assigned_brands || []); }}>Cancel</Button>
            <Button onClick={handleSaveBrands} disabled={loading} data-testid="save-brands-button">
              {loading ? 'Saving...' : 'Save Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfile;
